import React, { useState, useMemo } from 'react';
import { Search, Users } from 'lucide-react';
import FilterModalWrapper from './FilterModalWrapper';

/**
 * TeamModal - Desktop modal for selecting teams
 * Sport-agnostic component that can be used for NFL, NBA, NHL, etc.
 * Supports grouping by conference/division if provided
 * Adapted from mobile TeamSheet
 *
 * @param {boolean} isOpen - Whether modal is open
 * @param {Function} onClose - Callback to close modal
 * @param {Object} selectedTeam - Currently selected team
 * @param {Function} onTeamSelect - Callback when team is selected
 * @param {Array} teams - List of teams
 * @param {string} sport - Current sport context
 */
const TeamModal = ({
  isOpen,
  onClose,
  selectedTeam,
  onTeamSelect,
  teams = [],
  sport = 'nfl'
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Group teams by conference if they have that property
  const groupedTeams = useMemo(() => {
    if (!teams || teams.length === 0) return {};

    // Check if teams have conference/group data
    const hasConferences = teams.some(team => team.conference || team.group);

    if (!hasConferences) {
      return { 'All Teams': teams };
    }

    // Group by conference
    const grouped = {};
    teams.forEach(team => {
      const conference = team.conference || team.group || 'Other';
      if (!grouped[conference]) {
        grouped[conference] = [];
      }
      grouped[conference].push(team);
    });

    // Sort conferences (AFC before NFC for NFL, Eastern before Western for NBA)
    const sortedGroups = {};
    Object.keys(grouped).sort().forEach(key => {
      sortedGroups[key] = grouped[key].sort((a, b) =>
        (a.name || a.displayName || '').localeCompare(b.name || b.displayName || '')
      );
    });

    return sortedGroups;
  }, [teams]);

  // Filter teams by search query
  const filteredGroups = useMemo(() => {
    if (!searchQuery) return groupedTeams;

    const query = searchQuery.toLowerCase();
    const filtered = {};

    Object.entries(groupedTeams).forEach(([conference, teamList]) => {
      const filteredTeams = teamList.filter(team => {
        const name = (team.name || team.displayName || '').toLowerCase();
        const city = (team.city || '').toLowerCase();
        const code = (team.code || '').toLowerCase();
        return name.includes(query) || city.includes(query) || code.includes(query);
      });

      if (filteredTeams.length > 0) {
        filtered[conference] = filteredTeams;
      }
    });

    return filtered;
  }, [groupedTeams, searchQuery]);

  const handleTeamSelect = (team) => {
    onTeamSelect(team);
    setSearchQuery('');
  };

  const handleClearSelection = () => {
    onTeamSelect(null);
    setSearchQuery('');
  };

  return (
    <FilterModalWrapper
      isOpen={isOpen}
      onClose={onClose}
      title="Select Team"
      icon={Users}
      maxWidth="md"
      maxHeight="70vh"
    >
      {/* Search Bar */}
      <div className="p-4 border-b border-white/10">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search teams..."
            className="w-full pl-10 pr-4 py-2 bg-gray-800 text-white rounded-lg text-sm
                     focus:outline-none focus:ring-2 focus:ring-emerald-500
                     border border-white/10"
          />
        </div>
      </div>

      {/* Team List */}
      <div className="overflow-y-auto">
        {/* All Teams Option */}
        <button
          onClick={handleClearSelection}
          className={`
            w-full flex items-center gap-3 px-4 py-3 transition-colors
            ${!selectedTeam
              ? 'bg-emerald-500/20 text-emerald-400 border-l-4 border-emerald-500'
              : 'hover:bg-gray-800/50 text-white'
            }
          `}
        >
          <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center">
            <Users className="w-4 h-4 text-gray-400" />
          </div>
          <span className="font-medium">All Teams</span>
          {!selectedTeam && (
            <div className="ml-auto w-2 h-2 rounded-full bg-emerald-400" />
          )}
        </button>

        {/* Grouped Teams */}
        {Object.entries(filteredGroups).map(([conference, teamList]) => (
          <div key={conference}>
            {Object.keys(groupedTeams).length > 1 && (
              <div className="px-4 py-2 bg-gray-800/50 border-y border-white/5">
                <span className="text-xs font-semibold text-gray-400 uppercase">
                  {conference}
                </span>
              </div>
            )}

            {teamList.map((team) => {
              const isSelected = selectedTeam?.id === team.id;
              const teamName = team.name || team.displayName || 'Unknown Team';
              const teamLogo = team.logo || team.image;

              return (
                <button
                  key={team.id}
                  onClick={() => handleTeamSelect(team)}
                  className={`
                    w-full flex items-center gap-3 px-4 py-3 transition-colors
                    ${isSelected
                      ? 'bg-emerald-500/20 text-emerald-400 border-l-4 border-emerald-500'
                      : 'hover:bg-gray-800/50 text-white'
                    }
                  `}
                >
                  {teamLogo ? (
                    <img
                      src={teamLogo}
                      alt={teamName}
                      className="w-8 h-8 object-contain"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center">
                      <span className="text-xs font-bold text-gray-400">
                        {teamName.charAt(0)}
                      </span>
                    </div>
                  )}

                  <div className="flex-1 text-left">
                    <div className="font-medium">{teamName}</div>
                    {team.city && (
                      <div className="text-xs text-gray-500">{team.city}</div>
                    )}
                  </div>

                  {isSelected && (
                    <div className="w-2 h-2 rounded-full bg-emerald-400" />
                  )}
                </button>
              );
            })}
          </div>
        ))}

        {Object.keys(filteredGroups).length === 0 && (
          <div className="p-8 text-center">
            <p className="text-gray-500">No teams found</p>
          </div>
        )}
      </div>
    </FilterModalWrapper>
  );
};

export default TeamModal;
